import { useQuery } from '@tanstack/react-query'
import { useState, useCallback, useMemo } from 'react'
import {
  fetchActionHistory,
  resolveTargetNames,
  partyActionKeys,
  type ActionHistoryFilters,
  type ActionTargetNames,
} from '@/services/partyActions'
import type { ActionType, ActionStatus, PartyAction } from '@/types'

const DEFAULT_PAGE_SIZE = 15

/**
 * Hook for fetching paginated, filterable action history for a party.
 * Manages filter state, pagination, and target name resolution.
 */
export function useActionHistory(partyId: string | undefined) {
  const [filters, setFilters] = useState<ActionHistoryFilters>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })

  const query = useQuery({
    queryKey: partyActionKeys.history(partyId!, filters),
    queryFn: () => fetchActionHistory(partyId!, filters),
    enabled: !!partyId,
  })

  // Resolve target names for the current page of actions
  const targetNamesQuery = useQuery({
    queryKey: ['actionTargetNames', query.data?.actions.map((a) => a.id)],
    queryFn: () => resolveTargetNames(query.data!.actions),
    enabled: !!query.data && query.data.actions.length > 0,
  })

  const targetNames: ActionTargetNames = targetNamesQuery.data ?? {}

  const setActionTypeFilter = useCallback((actionType: ActionType | undefined) => {
    setFilters((prev) => ({ ...prev, actionType, page: 0 }))
  }, [])

  const setStatusFilter = useCallback((status: ActionStatus | undefined) => {
    setFilters((prev) => ({ ...prev, status, page: 0 }))
  }, [])

  const setTickRange = useCallback((tickFrom?: number, tickTo?: number) => {
    setFilters((prev) => ({ ...prev, tickFrom, tickTo, page: 0 }))
  }, [])

  const setPage = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }, [])

  const resolveTargetName = useCallback(
    (action: PartyAction): string | null => {
      if (!action.target_id) {
        return action.target_type === 'national' ? 'National' : null
      }
      return targetNames[action.target_id] ?? null
    },
    [targetNames]
  )

  return useMemo(
    () => ({
      actions: query.data?.actions ?? [],
      totalCount: query.data?.totalCount ?? 0,
      page: filters.page,
      pageSize: filters.pageSize,
      totalPages: query.data?.totalPages ?? 0,
      isLoading: query.isLoading,
      error: query.error,
      filters,
      setActionTypeFilter,
      setStatusFilter,
      setTickRange,
      setPage,
      resolveTargetName,
    }),
    [
      query.data,
      query.isLoading,
      query.error,
      filters,
      setActionTypeFilter,
      setStatusFilter,
      setTickRange,
      setPage,
      resolveTargetName,
    ]
  )
}
